<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Header -->
    <ThreadsNav show-back :show-center="false">
      <template #right>
        <router-link to="/adoption/create" class="btn btn-primary btn-sm">
          + {{ $t("adoption.publishAdoption") }}
        </router-link>
      </template>
    </ThreadsNav>

    <div class="flex-1 overflow-hidden">
      <VanTabs
        v-model:active="activeTab"
        class="flex flex-col"
        @change="reload"
      >
        <VanTab
          :title="$t('adoption.adoptable')"
          name="adoptable"
          class="h-full"
        >
        </VanTab>
        <VanTab :title="$t('adoption.adopted')" name="adopted"> </VanTab>
        <VanTab :title="$t('adoption.favorite')" name="favorite"></VanTab>
        <VanTab :title="$t('adoption.listed')" name="listed"></VanTab>
        <VanTab :title="$t('adoption.unlisted')" name="unlisted"></VanTab>
        <VanTab :title="$t('adoption.myAdoption')" name="myAdoption"></VanTab>
      </VanTabs>

      <!-- Mate Type Filter -->
      <div class="px-4 py-2 bg-base-100" v-if="activeTab === 'adoptable'">
        <BoxedTab
          v-model:selected-idx="activeMateTypeIdx"
          @update:selected-idx="handleMateTypeChange"
        >
          <div v-for="item in MATE_TYPE_OPTIONS" :key="item.value">
            {{ item.label }}
          </div>
        </BoxedTab>
      </div>

      <ThreadList
        ref="scroller"
        :on-pull-load="reload"
        :on-scroll-load="loadNextPage"
        @on-scroll="(h) => (height = h)"
        class="w-full"
      >
        <MateAdoptionWaterfall
          ref="waterfall"
          :list="list"
          :activeTab="activeTab"
        />
      </ThreadList>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  readAdoptionFavoritePage,
  readMateAdoptionExplorePage,
  readMateAdoptionPage,
} from "@/apis/mate";
import { ThreadListInstance } from "@/components/mobile/ThreadList.vue";
import { AdoptionStatus } from "@/enums/mateEnums";
import { PageResponse } from "@/types/api-types";
import { MateAdoption } from "@/types/entity";
import { useRouteQuery } from "@vueuse/router";
import { nextTick, onActivated, onMounted, ref } from "vue";
import { MateType } from "@/enums/mateEnums";
import BoxedTab from "@/components/mobile/common/BoxedTab.vue";

const activeTab = ref<string>("adoptable");
const activeMateTypeIdx = ref<number>(0);
const scroller = ref<ThreadListInstance>();
const list = ref<MateAdoption[]>([]);
const waterfall = ref();
const height = ref(0);

const MATE_TYPE_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "original", label: "原创" },
  { value: "fanswork", label: "二创" },
];

const loadNextPage = async () => {
  const { length } = list.value;
  if (length !== 0 && length % 10 !== 0) return false;

  const page = length / 10 + 1;
  let currentTab = activeTab.value;
  let res: PageResponse<MateAdoption>;
  const baseOption = {
    pagination: { page, perPage: 10 },
    sort: { field: "dtUpdate", order: "desc" as "desc" | "asc" },
  };

  // 构建 mateType 筛选参数
  const getMateTypeFilter = () => {
    const selectedType = MATE_TYPE_OPTIONS[activeMateTypeIdx.value].value;
    if (selectedType === "original") return MateType.Original;
    if (selectedType === "fanswork") return MateType.Fanswork;
    return undefined; // "all" 时不传 mateType 参数
  };

  const mateType = getMateTypeFilter();
  if (activeTab.value === "adoptable") {
    const filter: { status: AdoptionStatus; mateType?: MateType } = {
      status: AdoptionStatus.Available,
    };
    if (mateType !== undefined) {
      filter.mateType = mateType;
    }
    res = await readMateAdoptionExplorePage({
      filter,
      ...baseOption,
    });
  } else if (activeTab.value === "adopted") {
    res = await readMateAdoptionExplorePage({
      filter: {
        status: AdoptionStatus.Adopted,
      },
      ...baseOption,
    });
  } else if (activeTab.value === "favorite") {
    res = await readAdoptionFavoritePage(baseOption);
  } else if (activeTab.value === "listed") {
    res = await readMateAdoptionPage({
      filter: { role: "publisher", status: AdoptionStatus.Available },
      ...baseOption,
    });
  } else if (activeTab.value === "unlisted") {
    res = await readMateAdoptionPage({
      filter: { role: "publisher", status: AdoptionStatus.Unlisted },
      ...baseOption,
    });
  } else if (activeTab.value === "myAdoption") {
    res = await readMateAdoptionPage({
      filter: { role: "adopter", status: AdoptionStatus.Adopted },
      ...baseOption,
    });
  }

  if (currentTab !== activeTab.value || mateType !== getMateTypeFilter())
    return true;

  list.value = [...list.value, ...res!.page.data];
  nextTick(() => {
    if (waterfall.value) {
      waterfall.value.rerender();
    }
  });
  return res!.page.data.length === 10;
};

const reload = async () => {
  list.value = [];
  scroller.value?.resetScrollLoadingState();
};

const handleMateTypeChange = async (idx: number) => {
  activeMateTypeIdx.value = idx;
  await reload();
};

const type = useRouteQuery("type");
onMounted(() => {
  if (type.value) {
    activeTab.value = type.value as string;
    type.value = "";
  }
  console.log(list.value);
});

onActivated(() => {
  if (height.value) {
    scroller.value?.scrollTo(height.value);
  }
});
</script>
